import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ChefHat, Phone } from "lucide-react";

export default function AuthModal() {
  const { authOpen, setAuthOpen, setSession, authPrefill, setAuthPrefill } = useAuth();
  const [gName, setGName] = useState("");
  const [gEmail, setGEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pName, setPName] = useState("");
  const [otp, setOtp] = useState("");
  const [aEmail, setAEmail] = useState("");
  const [aPass, setAPass] = useState("");
  const [stage, setStage] = useState("phone"); // phone | otp
  const [loading, setLoading] = useState(false);
  const [defaultTab, setDefaultTab] = useState("google");

  // Apply prefill (e.g. admin quick-access) when modal opens
  React.useEffect(() => {
    if (authOpen && authPrefill) {
      if (authPrefill.name) setGName(authPrefill.name);
      if (authPrefill.email) setGEmail(authPrefill.email);
      if (authPrefill.tab) setDefaultTab(authPrefill.tab);
    }
  }, [authOpen, authPrefill]);

  const closeAll = () => {
    setAuthOpen(false);
    setAuthPrefill && setAuthPrefill(null);
    setGName(""); setGEmail(""); setPhone(""); setPName(""); setOtp(""); setStage("phone");
    setAEmail(""); setAPass("");
    setDefaultTab("google");
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const doLogin = async (e) => {
    e.preventDefault();
    if (!aEmail || !aPass) return toast.error("Enter email & password");
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email: aEmail, password: aPass });
      setSession(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.name}`);
      closeAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!phone) return toast.error("Enter phone");
    setLoading(true);
    try {
      const r = await api.post("/auth/phone/request", { phone, name: pName });
      toast.success("OTP sent");
      if (r.data.hint_code) toast.info(`Mocked OTP: ${r.data.hint_code}`);
      setStage("otp");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/phone/verify", { phone, code: otp, name: pName });
      setSession(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.name}`);
      closeAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent
        className="max-w-md bg-[#0F0F0F] border border-white/10 rounded-none p-0 overflow-hidden"
        data-testid="auth-modal"
      >
        <div className="p-8">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <img
                src="/chef-hire-logo.png"
                alt="Chef Hire"
                className="h-14 w-14 object-contain"
              />
              <div>
                <div className="font-script text-2xl -mb-1">Chef</div>
                <div className="small-caps text-[10px] text-neutral-400">Hire</div>
              </div>
            </div>
            <DialogTitle className="font-serif-display text-3xl mt-4">Welcome home.</DialogTitle>
            <p className="text-neutral-400 text-sm mt-1">Sign in to book a chef or plan your dinner.</p>
          </DialogHeader>

          <Tabs value={defaultTab} onValueChange={setDefaultTab} className="mt-6">
            <TabsList className="grid w-full grid-cols-2 bg-transparent rounded-none border border-white/10 p-0 h-11">
              <TabsTrigger value="google" data-testid="tab-google" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 data-[state=active]:text-white text-neutral-400 h-full">
                Google
              </TabsTrigger>
              <TabsTrigger value="admin" data-testid="tab-admin" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 data-[state=active]:text-white text-neutral-400 h-full">
                Email
              </TabsTrigger>
            </TabsList>

            <TabsContent value="google" className="mt-6">
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={startGoogle}
                  data-testid="google-submit"
                  className="w-full flex items-center justify-center gap-3 rounded-none border border-white/15 hover:border-white/40 bg-white text-neutral-900 h-11 small-caps text-[11px] font-medium transition-colors"
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
                  Continue with Google
                </button>
                <p className="text-[11px] text-neutral-400 text-center leading-relaxed">
                  Sign in securely with your Google account. We'll never post anything or see your password.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="admin" className="mt-6">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Email</Label>
                  <Input type="email" value={aEmail} onChange={(e) => setAEmail(e.target.value)} data-testid="admin-email"
                    className="mt-2 bg-transparent rounded-none border-white/10 focus-visible:border-[#C04000] focus-visible:ring-0" placeholder="you@domain.com" />
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Password</Label>
                  <Input type="password" value={aPass} onChange={(e) => setAPass(e.target.value)} data-testid="admin-password"
                    className="mt-2 bg-transparent rounded-none border-white/10 focus-visible:border-[#C04000] focus-visible:ring-0" placeholder="••••••••" />
                </div>
                <Button type="submit" disabled={loading} data-testid="admin-submit"
                  className="w-full rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">
                  Sign in
                </Button>
                <p className="text-[10px] text-neutral-500 text-center">Team, admin &amp; demo sign-in.</p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
